import StyledInput from '@/components/styled/StyledInput';
import StyledTextarea from '@/components/styled/StyledTextarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from '@/i18n';
import clsx from 'clsx';
import {
  agencyMutedFieldBoxClass,
  agencyMutedFieldContentClass,
  agencyMutedLabelClass,
} from '@/utils/estimates/agencyMutedFieldStyles';

interface EstimateCustomParagraphCardProps {
  control: Control<EstimateFormData>;
  isViewMode?: boolean;
  /** Disabled inputs (e.g. TO preview of agency-owned flight text). */
  readOnly?: boolean;
  /** Read-only display with muted text (agency/TO preview of the other party's content). */
  muted?: boolean;
  /** When readOnly, render even if values are empty. */
  showWhenEmpty?: boolean;
}

const EstimateCustomParagraphCard = ({
  control,
  isViewMode = false,
  readOnly = false,
  muted = false,
  showWhenEmpty = false,
}: EstimateCustomParagraphCardProps) => {
  const { t } = useTranslation();

  const customParagraphTitle = useWatch({
    control,
    name: 'customParagraphTitle',
  });
  const customParagraphDescription = useWatch({
    control,
    name: 'customParagraphDescription',
  });

  const titleText = customParagraphTitle?.toString().trim() ?? '';
  const descriptionText = customParagraphDescription?.toString().trim() ?? '';

  const hasContent = Boolean(titleText) || Boolean(descriptionText);

  if (readOnly && !showWhenEmpty && !hasContent) return null;

  const titleClass = muted ? agencyMutedLabelClass : 'text-black';
  const labelClass = muted ? agencyMutedLabelClass : 'text-gray-700';
  const mutedInputClass =
    muted && readOnly
      ? '[&_label]:text-gray-500 [&_input]:text-gray-500 [&_textarea]:text-gray-500'
      : undefined;

  if (isViewMode) {
    return (
      <ViewEstimateCustomParagraph
        muted={muted}
        customParagraphTitle={titleText || undefined}
        customParagraphDescription={descriptionText || undefined}
        showWhenEmpty={showWhenEmpty}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex gap-1 items-center">
          <h1 className={clsx('text-xl font-medium', titleClass)}>
            {t('estimates.details.customParagraph.title')}
          </h1>
          <span className={clsx('text-sm font-normal', titleClass)}>
            {t('estimates.details.customParagraph.optional')}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className={clsx('flex flex-col gap-4', mutedInputClass)}>
        <StyledInput
          control={control}
          name="customParagraphTitle"
          title={t('estimates.details.customParagraph.titleField')}
          titleClassName={labelClass}
          disabled={readOnly}
        />
        <StyledTextarea
          control={control}
          name="customParagraphDescription"
          title={t('estimates.details.customParagraph.descriptionField')}
          rows={3}
          disabled={readOnly}
        />
      </CardContent>
    </Card>
  );
};

export default EstimateCustomParagraphCard;

const ViewEstimateCustomParagraph = ({
  muted = false,
  customParagraphTitle,
  customParagraphDescription,
  showWhenEmpty = false,
}: {
  muted?: boolean;
  customParagraphTitle?: string;
  customParagraphDescription?: string;
  showWhenEmpty?: boolean;
}) => {
  const hasTitle = Boolean(customParagraphTitle?.trim());
  const hasDescription = Boolean(customParagraphDescription?.trim());

  if (!hasTitle && !hasDescription && !showWhenEmpty) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row justify-between">
        <CardTitle>
          <h1
            className={clsx(
              'text-xl font-medium',
              muted ? agencyMutedLabelClass : 'text-black',
            )}
          >
            {hasTitle ? customParagraphTitle : '—'}
          </h1>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <p
          className={clsx(
            muted
              ? clsx(agencyMutedFieldBoxClass, agencyMutedFieldContentClass)
              : 'whitespace-pre-wrap text-base text-gray-900',
          )}
        >
          {hasDescription ? customParagraphDescription : '—'}
        </p>
      </CardContent>
    </Card>
  );
};
